import React from 'react';
import { styles } from './SlideoutLayout.css';
import { AlgoliaProductHit } from '@jbhi-fi/apis/src/algolia/types/AlgoliaProductHit';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import { CouponsContainer } from '../../CouponsContainer';
import { Coupon, CouponOffering } from '../../types';
import { TITLE_ATTACH_SLIDEOUT } from '../../CouponsContainer/constants';
import { AddedToCartAlert } from '../../AddedToCartAlert';
import { CouponProgressBanner } from '../../CouponProgressBanner';

export type SlideoutLayoutProps = {
  heading: string;
  product: AlgoliaProductHit;
  onCheckoutClick: React.MouseEventHandler<HTMLButtonElement>;
  onReviewCartClick: React.MouseEventHandler<HTMLButtonElement>;
  onContinueShoppingClick: React.MouseEventHandler<HTMLButtonElement>;
  children: React.ReactNode;
  freeBonusProducts?: Array<AlgoliaProductHit> | null | undefined;
  isCouponSurfacingEnabled?: boolean;
  coupons?: Array<Coupon>;
  onHandleSelectCoupon: (couponCode: string) => void;
  isPerks?: boolean;
  shouldShowButtons?: boolean;
  couponOffering?: CouponOffering;
  isESimOnly?: boolean;
  isContentFullWidth?: boolean;
};

export const SlideoutLayout = ({
  children,
  heading,
  onCheckoutClick,
  onReviewCartClick,
  onContinueShoppingClick,
  product,
  freeBonusProducts,
  isCouponSurfacingEnabled,
  coupons,
  onHandleSelectCoupon,
  isPerks,
  shouldShowButtons = true,
  couponOffering,
  isESimOnly,
  isContentFullWidth = false,
}: SlideoutLayoutProps) => {
  return (
    <div className={styles.layout} data-testid="slideout-layout">
      {couponOffering?.coupon.isApplied && couponOffering?.coupon && couponOffering?.shouldShowSpendAndGetBanner ? (
        <CouponProgressBanner
          variant="mini"
          title={couponOffering.coupon.content.displayName}
          expiryDate={couponOffering.coupon.content.expiryDateFormatted ?? couponOffering.coupon.expiryDate}
          isApplied={couponOffering.coupon.isApplied}
          discount={couponOffering.coupon.discount}
          targetValue={couponOffering.coupon.minimumSpend}
          value={couponOffering.currentSpend}
          hasWarning={couponOffering.isProductExcluded}
        />
      ) : null}
      <AddedToCartAlert
        onCheckoutClick={onCheckoutClick}
        onReviewCartClick={onReviewCartClick}
        onContinueShoppingClick={onContinueShoppingClick}
        productImageSrc={product.product_image}
        productTitle={product.title}
        bonusProducts={freeBonusProducts}
        shouldShowButtons={shouldShowButtons}
        isESimOnly={isESimOnly}
      />
      {isCouponSurfacingEnabled && isPerks && coupons && coupons.length > 0 ? (
        <div className={styles.coupons}>
          <CouponsContainer
            variant="mini"
            coupons={coupons}
            title={TITLE_ATTACH_SLIDEOUT}
            shouldShowSubheading={false}
            onApplyCoupon={onHandleSelectCoupon}
          />
        </div>
      ) : null}
      {heading ? (
        <CalloutFont as="h1" className={styles.heading} testId="slideout-heading">
          {heading}
        </CalloutFont>
      ) : null}
      <div className={styles.content({ isContentFullWidth })}>{children}</div>

      <div className={styles.actions} data-testid="slideout-actions">
        <Button
          type="action"
          classes={`${styles.primaryActionButton} ${styles.checkoutButton}`}
          label="Checkout"
          onClick={onCheckoutClick}
          data-testid="slideout-checkout-button"
        />
        <Button
          type="primary"
          classes={styles.reviewButton}
          label="Review cart"
          onClick={onReviewCartClick}
          data-testid="slideout-review-button"
        />
        <Button
          type="link"
          classes={`${styles.secondaryActionButton} ${styles.continueButton}`}
          label="Continue shopping"
          onClick={onContinueShoppingClick}
          data-testid="slideout-continue-shopping-button"
        />
      </div>
    </div>
  );
};
